import type { Department } from "../CompleteProfile";
import {
  chatExampleCatalog,
  type ChatExample,
} from "../PersonalChats/chatExampleCatalog";
import { pickChatExamples, shuffle } from "../PersonalChats/chatExamplePrompts";

export type SignupRole = { department: string; seniority: string };

export type OnboardingPrompt = ChatExample & { kind: "workflow" | "app" };

type DepartmentPrompts = {
  team: string;
  focus: string;
  individual: readonly ChatExample[];
  leader: readonly ChatExample[];
  apps: readonly ChatExample[];
};

const APP_COUNT = 2;

const departmentPrompts: Record<
  Exclude<Department, "Other">,
  DepartmentPrompts
> = {
  IT: {
    team: "IT",
    focus: "keep tickets moving, manage access, and stay on top of devices",
    individual: [
      {
        connectors: ["okta"],
        prompt: "When someone leaves, offboard them from *Okta*.",
      },
      {
        connectors: ["jira"],
        prompt: "Triage new *Jira* IT tickets and assign owners.",
      },
      {
        connectors: ["jamf"],
        prompt: "Every morning, flag *Jamf* devices missing OS updates.",
      },
      {
        connectors: ["serviceNow"],
        prompt: "Route new *ServiceNow* incidents to the right team.",
      },
    ],
    leader: [
      {
        connectors: ["serviceNow"],
        prompt: "Every Monday, send me a *ServiceNow* backlog report.",
      },
      {
        connectors: ["okta", "slack"],
        prompt: "Post a daily digest of *Okta* access changes to *Slack*.",
      },
      {
        connectors: ["jira"],
        prompt: "Escalate *Jira* IT requests before they breach SLA.",
      },
      {
        connectors: ["googleSheets"],
        prompt: "Track upcoming software renewals in *Google Sheets*.",
      },
    ],
    apps: [
      {
        connectors: ["okta"],
        prompt: "Build an access request app with approvals for *Okta* groups.",
      },
      {
        connectors: ["jamf"],
        prompt: "Build a device inventory dashboard backed by *Jamf*.",
      },
      {
        connectors: ["serviceNow"],
        prompt: "Build an IT help portal that files *ServiceNow* tickets.",
      },
    ],
  },
  Security: {
    team: "security",
    focus:
      "triage detections, review risky access, and chase down vulnerabilities",
    individual: [
      {
        connectors: ["crowdStrike"],
        prompt: "Enrich new *CrowdStrike* detections with threat intel.",
      },
      {
        connectors: ["okta"],
        prompt: "When *Okta* sees a suspicious sign-in, lock the account.",
      },
      {
        connectors: ["splunk"],
        prompt: "Alert me when *Splunk* logs repeated failed admin logins.",
      },
      {
        connectors: ["wiz"],
        prompt: "Open a ticket for every new critical *Wiz* issue.",
      },
    ],
    leader: [
      {
        connectors: ["crowdStrike"],
        prompt: "Send a weekly digest of *CrowdStrike* detection trends.",
      },
      {
        connectors: ["wiz"],
        prompt: "Every Monday, report open *Wiz* issues by owning team.",
      },
      {
        connectors: ["pagerDuty"],
        prompt: "Summarize each day’s security incidents from *PagerDuty*.",
      },
      {
        connectors: ["jira", "slack"],
        prompt: "Post a morning *Jira* security briefing to *Slack*.",
      },
    ],
    apps: [
      {
        connectors: ["okta"],
        prompt: "Build an access review app for *Okta* group memberships.",
      },
      {
        connectors: ["crowdStrike"],
        prompt: "Build a triage console for *CrowdStrike* detections.",
      },
      {
        connectors: ["wiz"],
        prompt:
          "Build a vulnerability tracker that groups *Wiz* issues by owner.",
      },
    ],
  },
  Finance: {
    team: "finance",
    focus: "track spend, chase overdue invoices, and keep budgets on plan",
    individual: [
      {
        connectors: ["netSuite"],
        prompt: "Chase *NetSuite* invoices once they’re 30 days overdue.",
      },
      {
        connectors: ["stripe"],
        prompt: "Every morning, flag failed *Stripe* payments.",
      },
      {
        connectors: ["quickbooks"],
        prompt: "Categorize new *QuickBooks* expenses as they come in.",
      },
      {
        connectors: ["googleSheets"],
        prompt: "Each night, reconcile actual spend into *Google Sheets*.",
      },
    ],
    leader: [
      {
        connectors: ["netSuite"],
        prompt: "Send a monthly *NetSuite* spend report by department.",
      },
      {
        connectors: ["stripe"],
        prompt: "Every Monday, report *Stripe* MRR against last month.",
      },
      {
        connectors: ["salesforce"],
        prompt: "Email a weekly *Salesforce* bookings forecast.",
      },
      {
        connectors: ["googleSheets"],
        prompt: "Flag budget lines in *Google Sheets* as they go over plan.",
      },
    ],
    apps: [
      {
        connectors: ["netSuite"],
        prompt: "Build a spend approval app that checks budgets in *NetSuite*.",
      },
      {
        connectors: ["stripe"],
        prompt: "Build a revenue dashboard from *Stripe* subscriptions.",
      },
      {
        connectors: ["googleSheets"],
        prompt: "Build an expense report app that logs to *Google Sheets*.",
      },
    ],
  },
  HR: {
    team: "HR",
    focus:
      "onboard new hires, keep hiring moving, and stay on top of your people data",
    individual: [
      {
        connectors: ["workday"],
        prompt: "When a hire lands in *Workday*, kick off their onboarding.",
      },
      {
        connectors: ["greenhouse"],
        prompt: "Nudge interviewers who owe *Greenhouse* feedback.",
      },
      {
        connectors: ["bambooHr"],
        prompt: "Celebrate *BambooHR* work anniversaries automatically.",
      },
      {
        connectors: ["googleCalendar"],
        prompt: "Book new-hire onboarding sessions in *Google Calendar*.",
      },
    ],
    leader: [
      {
        connectors: ["workday"],
        prompt: "Send a monthly headcount report from *Workday*.",
      },
      {
        connectors: ["greenhouse"],
        prompt: "Flag candidates stalled in our *Greenhouse* pipeline.",
      },
      {
        connectors: ["workday"],
        prompt: "Every Monday, report open roles by team from *Workday*.",
      },
      {
        connectors: ["slack"],
        prompt: "Welcome each week’s new hires in *Slack* automatically.",
      },
    ],
    apps: [
      {
        connectors: ["workday"],
        prompt: "Build a new-hire onboarding checklist backed by *Workday*.",
      },
      {
        connectors: ["greenhouse"],
        prompt: "Build an interview scorecard app that syncs to *Greenhouse*.",
      },
      {
        connectors: ["bambooHr"],
        prompt: "Build a time-off request app that checks *BambooHR* balances.",
      },
    ],
  },
  RevOps: {
    team: "RevOps",
    focus:
      "keep your pipeline clean, route leads, and forecast with confidence",
    individual: [
      {
        connectors: ["salesforce"],
        prompt: "Flag *Salesforce* deals that haven’t moved in two weeks.",
      },
      {
        connectors: ["hubSpot"],
        prompt: "Assign new *HubSpot* leads to the right owner instantly.",
      },
      {
        connectors: ["gong"],
        prompt: "After each *Gong* call, draft my follow-up email.",
      },
      {
        connectors: ["salesforce"],
        prompt: "Merge duplicate *Salesforce* accounts every night.",
      },
    ],
    leader: [
      {
        connectors: ["salesforce"],
        prompt: "Report weekly *Salesforce* pipeline coverage by rep.",
      },
      {
        connectors: ["gong"],
        prompt: "Summarize themes from each week’s *Gong* calls.",
      },
      {
        connectors: ["hubSpot"],
        prompt: "Track *HubSpot* lead conversion and alert me on dips.",
      },
      {
        connectors: ["salesforce", "slack"],
        prompt: "Post the weekly *Salesforce* forecast to *Slack*.",
      },
    ],
    apps: [
      {
        connectors: ["salesforce"],
        prompt: "Build a deal desk app for *Salesforce* discount approvals.",
      },
      {
        connectors: ["hubSpot"],
        prompt: "Build a lead routing app that assigns *HubSpot* leads.",
      },
      {
        connectors: ["salesforce"],
        prompt: "Build a pipeline dashboard from *Salesforce* opportunities.",
      },
    ],
  },
  Engineering: {
    team: "engineering",
    focus: "review code, triage errors, and keep on-call calm",
    individual: [
      {
        connectors: ["github"],
        prompt: "Remind me about *GitHub* reviews waiting more than a day.",
      },
      {
        connectors: ["sentry"],
        prompt: "Triage new *Sentry* errors into issues automatically.",
      },
      {
        connectors: ["linear"],
        prompt: "Turn bug reports into *Linear* issues as they come in.",
      },
      {
        connectors: ["datadog"],
        prompt: "Enrich *Datadog* alerts with likely causes before paging.",
      },
    ],
    leader: [
      {
        connectors: ["linear"],
        prompt: "Send a weekly *Linear* cycle progress report.",
      },
      {
        connectors: ["github"],
        prompt: "Every Monday, report the oldest open *GitHub* PRs.",
      },
      {
        connectors: ["pagerDuty"],
        prompt: "Summarize each week’s on-call load from *PagerDuty*.",
      },
      {
        connectors: ["sentry"],
        prompt: "Rank services by new *Sentry* errors every week.",
      },
    ],
    apps: [
      {
        connectors: ["github"],
        prompt: "Build a release-notes app that compares *GitHub* tags.",
      },
      {
        connectors: ["linear"],
        prompt: "Build a team workload dashboard from *Linear* issues.",
      },
      {
        connectors: ["pagerDuty"],
        prompt: "Build an on-call handoff app from *PagerDuty* schedules.",
      },
    ],
  },
  Product: {
    team: "product",
    focus:
      "track the roadmap, make sense of customer feedback, and keep launches on track",
    individual: [
      {
        connectors: ["linear"],
        prompt: "Write weekly release notes from what shipped in *Linear*.",
      },
      {
        connectors: ["zendesk"],
        prompt: "Tag new *Zendesk* feature requests by theme.",
      },
      {
        connectors: ["figma"],
        prompt: "Send me a daily digest of new *Figma* comments.",
      },
      {
        connectors: ["notion"],
        prompt: "Turn *Notion* meeting notes into tasks automatically.",
      },
    ],
    leader: [
      {
        connectors: ["linear"],
        prompt: "Every Friday, report roadmap progress from *Linear*.",
      },
      {
        connectors: ["intercom"],
        prompt: "Surface weekly themes from *Intercom* conversations.",
      },
      {
        connectors: ["jira"],
        prompt: "Flag *Jira* epics as soon as they’re at risk of slipping.",
      },
      {
        connectors: ["notion", "slack"],
        prompt: "Post a weekly product update from *Notion* to *Slack*.",
      },
    ],
    apps: [
      {
        connectors: ["linear"],
        prompt: "Build a roadmap explorer from *Linear* projects.",
      },
      {
        connectors: ["zendesk"],
        prompt:
          "Build a feedback inbox that groups *Zendesk* requests by theme.",
      },
      {
        connectors: ["notion"],
        prompt: "Build a launch checklist app backed by *Notion*.",
      },
    ],
  },
  Legal: {
    team: "legal",
    focus: "move contracts along, track renewals, and review agreements faster",
    individual: [
      {
        connectors: ["docusign"],
        prompt: "Chase *DocuSign* envelopes unsigned after three days.",
      },
      {
        connectors: ["ironclad"],
        prompt: "Summarize the key terms of each new *Ironclad* contract.",
      },
      {
        connectors: ["googleDocs"],
        prompt: "Flag risky clauses in new *Google Docs* contracts.",
      },
      {
        connectors: ["salesforce"],
        prompt: "Route *Salesforce* deals that need legal review to me.",
      },
    ],
    leader: [
      {
        connectors: ["ironclad"],
        prompt: "Alert me 90 days before *Ironclad* contracts renew.",
      },
      {
        connectors: ["docusign"],
        prompt: "Send a monthly report of *DocuSign* turnaround times.",
      },
      {
        connectors: ["jira"],
        prompt: "Triage the legal request queue in *Jira* automatically.",
      },
      {
        connectors: ["ironclad", "slack"],
        prompt: "Post a weekly *Ironclad* contract summary to *Slack*.",
      },
    ],
    apps: [
      {
        connectors: ["ironclad"],
        prompt: "Build a contract request intake app that files to *Ironclad*.",
      },
      {
        connectors: ["docusign"],
        prompt:
          "Build an NDA generator that sends for signature with *DocuSign*.",
      },
      {
        connectors: ["googleDrive"],
        prompt: "Build a clause library app backed by *Google Drive*.",
      },
    ],
  },
};

function promptsFor(role: SignupRole | null) {
  return role
    ? (departmentPrompts as Partial<Record<string, DepartmentPrompts>>)[
        role.department
      ]
    : undefined;
}

export function departmentIntro(role: SignupRole | null) {
  const prompts = promptsFor(role);
  return prompts ? { team: prompts.team, focus: prompts.focus } : null;
}

// Fully connected ideas come first; general ones fill at most half the slots so the role still shows through.
function preferConnected(
  roleIdeas: readonly ChatExample[],
  departmentIdeas: readonly ChatExample[],
  generalIdeas: readonly ChatExample[],
  connectorIdentifiers: readonly string[],
  count: number,
  usedLeads: ReadonlySet<string | undefined> = new Set()
): ChatExample[] {
  const connected = new Set(connectorIdentifiers);
  const picked: ChatExample[] = [];
  const leads = new Set(usedLeads);
  const takeConnected = (ideas: readonly ChatExample[], limit: number) => {
    for (const idea of ideas) {
      if (picked.length >= limit) return;
      const lead = idea.connectors[0];
      if (leads.has(lead) || !idea.connectors.every((c) => connected.has(c)))
        continue;
      leads.add(lead);
      picked.push(idea);
    }
  };
  takeConnected(roleIdeas, count);
  takeConnected(departmentIdeas, count);
  takeConnected(generalIdeas, picked.length + Math.floor(count / 2));
  return [
    ...picked,
    ...roleIdeas
      .filter((idea) => !picked.includes(idea))
      .toSorted(
        (a, b) =>
          Number(leads.has(a.connectors[0])) -
          Number(leads.has(b.connectors[0]))
      ),
  ].slice(0, count);
}

// Two app ideas always make the cut, set second and second-to-last so they read as part of the mix.
export function pickOnboardingPrompts(
  role: SignupRole | null,
  connectorIdentifiers: readonly string[],
  count: number,
  seed: string
): OnboardingPrompt[] {
  const prompts = promptsFor(role);
  const workflowCount = count - APP_COUNT;
  const individual = role?.seniority === "Individual contributor";
  const workflows = (
    prompts
      ? preferConnected(
          shuffle(individual ? prompts.individual : prompts.leader, seed),
          shuffle(individual ? prompts.leader : prompts.individual, seed),
          shuffle(chatExampleCatalog.workflows, seed),
          connectorIdentifiers,
          workflowCount
        )
      : pickChatExamples(connectorIdentifiers, workflowCount, seed, "workflows")
  ).map((example) => ({ ...example, kind: "workflow" as const }));
  const workflowLeads = new Set(
    workflows.map((example) => example.connectors[0])
  );
  const [firstApp, secondApp] = (
    prompts
      ? preferConnected(
          shuffle(prompts.apps, `${seed}:apps`),
          [],
          shuffle(chatExampleCatalog.apps, `${seed}:apps`),
          connectorIdentifiers,
          APP_COUNT,
          workflowLeads
        )
      : pickChatExamples(connectorIdentifiers, APP_COUNT, seed, "apps")
  ).map((example) => ({ ...example, kind: "app" as const }));
  const picked: OnboardingPrompt[] = [...workflows];
  if (firstApp) picked.splice(1, 0, firstApp);
  if (secondApp) picked.splice(picked.length - 1, 0, secondApp);
  return picked;
}
